import { Component, OnInit, OnDestroy } from '@angular/core'
import { Router } from '@angular/router'
import { Subscription, merge } from 'rxjs';
import { ModalController, Platform } from '@ionic/angular'
import Spriteling from 'spriteling'
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core'
import { Keepalive } from '@ng-idle/keepalive'
import { debounceTime, mapTo } from 'rxjs/operators';

// Service
import { AppService } from '@/services/app.service'
import { AuthService } from '@/services/auth.service'
import { BrowserService } from '@/services/browser.service'
import { NetworkService } from '@/services/network.service';

// Components
import { LogoutPopupComponent } from '../common/components/modal/logout-popup/logout-popup.component'

// Constants
import { LOGGING_OUT_PAGE_PUNS } from '@/constants/loading-puns.constants';
import { LOGOUT_TIMEOUT } from '@/constants/loading.constants';
import { NO_INTERNET_MESSAGE } from '@/constants/message.constants';
import { DeviceNetworkService } from '@/services/device-network.service';
import { OrientationService } from '@/services/orientation.service';
import { Capacitor } from '@capacitor/core';

declare let window: any;

const NETWORK_ALERT_DELAY = 1000;

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  browserSubscription: Subscription;
  browserDialogSubscription: Subscription;
  appServiceSubscription: Subscription;
  screenOrientationSubscription: Subscription;
  resizeSubscription: Subscription;
  networkSubscription: Subscription;

  showLoadingScreen: boolean = false
  isBrowserPlatform: boolean = false
  showMenu: boolean = false
  showIABLoading: boolean = false
  showIABDialog: boolean = false
  loadingPun: string = ''
  profileData: any

  dialog: any = {}

  // Viewport
  isMobile: boolean = false
  isLandscape: boolean = false
  deviceWidth: number
  isPhonePlatform: boolean

  // Idle
  idleState: boolean = false
  showIdlePopup: boolean = false

  constructor(
    private screenOrientation: OrientationService,
    public modalController: ModalController,
    public browserService: BrowserService,
    public appService: AppService,
    private keepalive: Keepalive,
    public platform: Platform,
    public auth: AuthService,
    public router: Router,
    private idle: Idle,
    private networkService: NetworkService,
    private network: DeviceNetworkService
  ) {
    this.networkSubscription = this.network.getNetworkStatus()
      .pipe(debounceTime(NETWORK_ALERT_DELAY))
      .subscribe((state) => {
        if (state === 'connected') {
      this.networkService.connected();
        } else {
        this.networkService.disconnected();
      window.alert(NO_INTERNET_MESSAGE);
        }
    });

    this.platform.ready().then(() => {
      // this.screenOrientation.lock(this.screenOrientation.ORIENTATIONS.LANDSCAPE);

      const platforms = this.platform.platforms()
      this.isPhonePlatform = platforms.indexOf("iphone") > -1
        || platforms.indexOf("phablet") > -1

      this.isBrowserPlatform = Capacitor.getPlatform() === 'browser'

      this.isLandscape = this.platform.isLandscape()
      this.deviceWidth = this.platform.width()
      this.isMobile  = this.deviceWidth <= 428

      this.setDeviceAndOrientation(this.isLandscape, this.deviceWidth)

      this.screenOrientationSubscription = this.screenOrientation
        .orientationChangeSubject.asObservable()
        .subscribe(() => {

          setTimeout(() => {
            this.isBrowserPlatform = Capacitor.getPlatform() === 'browser'
            this.isLandscape = this.platform.isLandscape()
            this.appService.updateOrientation(this.isLandscape)

            this.deviceWidth = this.platform.width()
            this.isMobile  = this.deviceWidth <= 428

            this.appService.updateDeviceWidth(this.deviceWidth)
          }, 300)
        })

      this.resizeSubscription = this.platform
        .resize
        .subscribe(() => {
          setTimeout(() => {
            this.isLandscape = this.platform.isLandscape()
            this.appService.updateOrientation(this.isLandscape)

            this.deviceWidth = this.platform.width()
            this.isMobile  = this.deviceWidth <= 428
            this.appService.updateDeviceWidth(this.deviceWidth)
          }, 300)
        })

      this.platform.backButton.subscribeWithPriority(1, () => {
        this.navigateHome()
      });
    })

    // Set Idle
    idle.setIdle(900) // 15 mins
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES)

    idle.onIdleEnd.subscribe(() => {
      this.idleState = false
    })

    idle.onIdleStart.subscribe(() => {
      this.idleState = true
    })

    this.platform.resume.subscribe(() => {
      if (
        this.idleState
        && this.auth.isAuthenticated()
      ) {
        this.profileData = JSON.parse(sessionStorage.getItem("user.profile"))
        this.showIdlePopup = true
      } else {
        this.resetIdle()
        this.navigateHome()
      }
    });

    this.resetIdle()

    this.showMenu = false

    this.browserSubscription = this.browserService
      .getSiteLoading()
      .subscribe(showLoader => {
        if (showLoader) {
          this.showIABLoading = true

          setTimeout(() => {
            const sprite = new Spriteling({
              url: 'assets/img/loading_bees_spritesheet.png',
              top: 0,
              left: 0,
              cols: 3,
              rows: 4
            }, '#loading-sprite-bee')

            sprite.play({
              run: -1,
              delay: 140
            })

          }, 400)

        } else {
          this.showIABLoading = false

          setTimeout(() => {
            const spriteElement = (window.document.getElementById("loading-sprite-bee") as HTMLElement);
            if (spriteElement) {
              spriteElement.remove();
            }
          }, 400)
        }
      })

    this.browserDialogSubscription = this.browserService
      .getDialog()
      .subscribe(dialog => {
        this.dialog = dialog.dialog

        if (dialog) {
          this.showIABDialog = true

        } else {
          this.showIABDialog = false
        }
      })

    this.appServiceSubscription = this.appService
      .getPreview()
      .subscribe(({showPreview}) => {

        if (showPreview === 'show') {
          (window.document.querySelector("ion-app") as HTMLElement).classList.add("camera-view");
          window.document.body.style.backgroundColor = "transparent !important";
          (window.document.getElementById("main-content") as HTMLElement).classList.add("full-width");
          (window.document.getElementById("main-hex--container") as HTMLElement).classList.add("browser-large-view");
          (window.document.getElementById("main-menu--container") as HTMLElement).classList.add("camera-view");
          (window.document.getElementById("main-menu") as HTMLElement).classList.add("camera-view");
        } else {
          (window.document.querySelector("ion-app") as HTMLElement).classList.remove("camera-view");
          window.document.body.style.backgroundColor = '#FFF';
          (window.document.getElementById("main-hex--container") as HTMLElement).classList.remove("hide-display");

          if (Capacitor.getPlatform() === 'browser') {
            (window.document.getElementById("main-hex--container") as HTMLElement).className = '';
            (window.document.getElementById("main-hex--container") as HTMLElement).classList.add("browser-large-view");
          }

          (window.document.getElementById("main-content") as HTMLElement).classList.remove("full-width");
          (window.document.getElementById("main-menu") as HTMLElement).classList.remove("camera-view");
          (window.document.getElementById("main-menu--container") as HTMLElement).classList.remove("camera-view");
        }
      })
  }

  resetIdle() {
    this.idle.watch()
    this.idleState = false
    this.showIdlePopup = false
  }

  handleCloseDialog(terminate){
    this.showIABDialog = false

    if (!!this.browserService.iab){
      if (terminate){
         this.browserService.iab.close()

      } else {
        this.browserService.iab.show()
      }
    }
  }

  setDeviceAndOrientation(oritentation, deviceWidth) {
    this.appService.updateOrientation(oritentation)
    this.appService.updateDeviceWidth(deviceWidth)
  }

  navigateHome() {
    this.router.navigate(['home'])
  }

  async presentModal() {
    const modal = await this.modalController.create({
      component: LogoutPopupComponent,
      cssClass: 'confirm-logout-modal'
    })

    modal.present()
    const proceedLogout = await modal.onWillDismiss()

    if (
      proceedLogout
      && proceedLogout.data
      && proceedLogout.data.proceedLogout
    ) {
      this.onLogout()
    }
  }

  async ngOnInit() {
    await this.network.initializeNetworkEvents()
    if (this.auth.isAuthenticated()) {
      this.appService.showComponentPreview('hide')
    }
  }

  handleLogout() {
    this.resetIdle()
    this.onLogout()
  }

  onLogout() {
    this.auth.logout()
    this.loadingPun = this.getRandomPun()
    this.showMenu = false
    this.showLoadingScreen = true;

    (window.document.getElementById("main-hex--container") as HTMLElement).classList.add("hide-display");

    setTimeout(() => {
      this.router.navigate(['login']);
    }, LOGOUT_TIMEOUT - 800);

    setTimeout(() => {
      this.showLoadingScreen = false;
    }, LOGOUT_TIMEOUT);
  }

  toggleMenu() {
    this.showMenu = !this.showMenu
  }

  getRandomPun() {
    const listIndex = Math.floor(Math.random()*(6-1+1))+1
    return LOGGING_OUT_PAGE_PUNS[listIndex - 1]
  }

  handleNavigateApp() {
    const routeLocation = 'home'
    this.router.navigate([routeLocation])
  }

  ngOnDestroy() {
    if (this.browserSubscription) {
      this.browserSubscription.unsubscribe()
    }

    if (this.appServiceSubscription) {
      this.appServiceSubscription.unsubscribe()
    }

    if (this.browserDialogSubscription) {
      this.browserDialogSubscription.unsubscribe()
    }

    if (this.screenOrientationSubscription) {
      this.screenOrientationSubscription.unsubscribe()
    }

    this.networkSubscription?.unsubscribe();

    if (this.resizeSubscription) {
      this.resizeSubscription.unsubscribe()
    }
  }
}
